import { getAssetUrl } from './assets';

export const UPTIMIO_BRAND = {
  primaryColor: '#10b981',
  sidebarColor: '#0f7a56',
  sidebarTextColor: '#f8fafc',
  title: 'Helpdesk Uptimio',
  favicon: '/UptimioIcon.png'
};

export type BrandableCompany = {
  name?: string;
  logo?: string;
  icon?: string;
  branding?: {
    primaryColor?: string;
    sidebarColor?: string;
    sidebarTextColor?: string;
    buttonTextColor?: string;
  };
} | null | undefined;

export const parseHexColor = (value?: string | null): string | undefined => {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim();
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  if (!/^#[0-9a-fA-F]{6}$/.test(withHash)) {
    return undefined;
  }

  return withHash.toLowerCase();
};

const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const n = parseInt(hex.slice(1), 16);
  return {
    r: (n >> 16) & 255,
    g: (n >> 8) & 255,
    b: n & 255
  };
};

const rgbToHex = (r: number, g: number, b: number): string => {
  return `#${[r, g, b].map((channel) => Math.max(0, Math.min(255, channel)).toString(16).padStart(2, '0')).join('')}`;
};

const mixHex = (hex: string, withHex: string, amount: number): string => {
  const from = hexToRgb(hex);
  const to = hexToRgb(withHex);
  return rgbToHex(
    Math.round(from.r + (to.r - from.r) * amount),
    Math.round(from.g + (to.g - from.g) * amount),
    Math.round(from.b + (to.b - from.b) * amount)
  );
};

export const darkenHex = (hex: string, amount: number): string => mixHex(hex, '#000000', amount);

export const contrastTextOn = (hex: string): string => {
  const { r, g, b } = hexToRgb(hex);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.62 ? '#111827' : '#f8fafc';
};

const setFavicon = (href: string) => {
  const existing = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (existing) {
    existing.href = href;
    return;
  }

  const link = document.createElement('link');
  link.rel = 'icon';
  link.href = href;
  document.head.appendChild(link);
};

export const applyCompanyBrand = (company?: BrandableCompany) => {
  const customPrimary = parseHexColor(company?.branding?.primaryColor);
  const customSidebar = parseHexColor(company?.branding?.sidebarColor);
  const customSidebarText = parseHexColor(company?.branding?.sidebarTextColor);
  const customButtonText = parseHexColor(company?.branding?.buttonTextColor);
  const primary = customPrimary || UPTIMIO_BRAND.primaryColor;
  const sidebar = customSidebar || UPTIMIO_BRAND.sidebarColor;
  const sidebarText = customSidebarText || contrastTextOn(sidebar);
  const buttonText = customButtonText || contrastTextOn(primary);
  const primaryDark = darkenHex(primary, 0.12);
  const primaryDarker = darkenHex(primary, 0.22);
  const sidebarDark = darkenHex(sidebar, 0.18);
  const sidebarMid = mixHex(sidebar, primary, 0.35);
  const primaryRgb = hexToRgb(primary);
  const primaryDarkRgb = hexToRgb(primaryDark);
  const sidebarRgb = hexToRgb(sidebar);
  const sidebarDarkRgb = hexToRgb(sidebarDark);

  const root = document.documentElement;
  root.style.setProperty('--brand-primary', primary);
  root.style.setProperty('--brand-primary-dark', primaryDark);
  root.style.setProperty('--brand-primary-darker', primaryDarker);
  root.style.setProperty('--brand-sidebar', sidebar);
  root.style.setProperty('--brand-sidebar-dark', sidebarDark);
  root.style.setProperty('--brand-sidebar-mid', sidebarMid);
  root.style.setProperty('--brand-sidebar-text', sidebarText);
  root.style.setProperty('--brand-button-text', buttonText);
  root.style.setProperty('--brand-primary-rgb', `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
  root.style.setProperty('--brand-primary-dark-rgb', `${primaryDarkRgb.r}, ${primaryDarkRgb.g}, ${primaryDarkRgb.b}`);
  root.style.setProperty('--brand-sidebar-rgb', `${sidebarRgb.r}, ${sidebarRgb.g}, ${sidebarRgb.b}`);
  root.style.setProperty('--brand-sidebar-dark-rgb', `${sidebarDarkRgb.r}, ${sidebarDarkRgb.g}, ${sidebarDarkRgb.b}`);

  const branded = Boolean(company && (company.logo || company.icon || customPrimary || customSidebar || customSidebarText || customButtonText));
  document.title = branded && company?.name ? `${company.name} Helpdesk` : UPTIMIO_BRAND.title;

  const favicon = getAssetUrl(company?.icon) || getAssetUrl(company?.logo) || UPTIMIO_BRAND.favicon;
  setFavicon(favicon);

  const themeColor = document.querySelector('meta[name="theme-color"]');
  if (themeColor) {
    themeColor.setAttribute('content', sidebar);
  }
};

export const clearCompanyBrand = () => applyCompanyBrand(null);
